import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {CompanyModel} from '../../../models/api/companyModel';
import {ResourceListPagination, ResourceTableCommonPageType} from './resourceListPaginationHelpers';

/*
 * Consts.
 */

const DEFAULT_OFFSET_PAGE_SIZE = 50;

/*
 * Types.
 */

export interface PaginationParameter {
  offset: number;
  limit: number;
}

type ResourceListOffsetPageType<TResource, TFields> = ResourceTableCommonPageType<TResource> &
  TFields & {
    nextOffset: number;
    total?: number;
  };

interface ResourceListPaginationOffsetConfig<TResource, TFields> {
  pageSize?: number;
  fetchApi: (
    pagination: PaginationParameter,
    getNextOffset: (currentOffset: number) => number,
    company: CompanyModel,
    cancelToken: CancelToken,
  ) => Promise<ResourceListOffsetPageType<TResource, TFields>>;
}

/*
 * Helpers.
 */

/**
 * Abstracts the Offset Limit logic to useResourceList.
 *
 * @param config The config of the pagination.
 */
export function makeResourceListPaginationOffset<TResource, TFields = {}>(
  config: ResourceListPaginationOffsetConfig<TResource, TFields>,
): ResourceListPagination<TResource, number, ResourceListOffsetPageType<TResource, TFields>> {
  const {fetchApi, pageSize = DEFAULT_OFFSET_PAGE_SIZE} = config;
  const getNextOffset = (offset: number) => offset + pageSize;

  return {
    fetchApi: async (maybeOffset, company, cancelToken) =>
      fetchApi({offset: maybeOffset ?? 0, limit: pageSize}, getNextOffset, company, cancelToken),
    getNextPageParam: (lastPage) => {
      if (lastPage.total !== undefined) {
        if (lastPage.nextOffset > lastPage.total) {
          return undefined;
        }
      } else if (lastPage.items.length !== pageSize) {
        return undefined;
      }

      return lastPage.nextOffset;
    },
  };
}
